import { createSlice } from '@reduxjs/toolkit';

import {
  register,
  resendInvite,
  addPassword,
  login,
  current,
  logout,
  changePassword,
  requestPasswordReset,
  confirmResetCode,
  resetPassword,
  updateCurrent,
} from './auth-operations';

const initialState = {
  user: {},
  token: '',
  isLogin: false,
  isLoading: false,
  error: null,
  isResetCodeSent: false,
  resetEmail: '',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    setResetEmail: (state, action) => {
      state.resetEmail = action.payload;
    },
    clearResetState: state => {
      state.isResetCodeSent = false;
      state.resetEmail = '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(register.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, state => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(register.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(resendInvite.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendInvite.fulfilled, state => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resendInvite.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(addPassword.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPassword.fulfilled, (state, { payload }) => {
        const { token } = payload;
        state.isLoading = false;
        state.token = token;
        state.error = null;
      })
      .addCase(addPassword.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(login.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        const { admin, token } = payload;
        state.isLoading = false;
        state.user = admin;
        state.token = token;
        state.isLogin = true;
      })
      .addCase(login.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(current.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(current.fulfilled, (state, { payload }) => {
        const { admin } = payload;
        state.isLoading = false;
        state.user = admin;
        state.isLogin = true;
      })
      .addCase(current.rejected, state => {
        state.isLoading = false;
        state.isLogin = false;
        state.token = '';
      })
      .addCase(logout.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, state => {
        state.isLoading = false;
        state.user = {};
        state.token = null;
        state.isLogin = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      // Change Password
      .addCase(changePassword.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, state => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      // Request Password Reset
      .addCase(requestPasswordReset.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestPasswordReset.fulfilled, (state, { meta }) => {
        state.isLoading = false;
        state.error = null;
        state.isResetCodeSent = true;
        state.resetEmail = meta.arg.email;
      })
      .addCase(requestPasswordReset.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      // Confirm Reset Code
      .addCase(confirmResetCode.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmResetCode.fulfilled, state => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(confirmResetCode.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      // Reset Password
      .addCase(resetPassword.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, { payload }) => {
        const { token } = payload;
        state.isLoading = false;
        state.token = token;
        state.isLogin = true;
        state.error = null;
        state.isResetCodeSent = false;
        state.resetEmail = '';
      })
      .addCase(resetPassword.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      // Update Current User
      .addCase(updateCurrent.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCurrent.fulfilled, (state, { payload }) => {
        const { admin } = payload;
        state.isLoading = false;
        state.user = admin;
        state.error = null;
      })
      .addCase(updateCurrent.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      });
  },
});

export const { setError, clearError, setResetEmail, clearResetState } = authSlice.actions;

export default authSlice.reducer;
